import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { termsOfUse } from '../content/terms-of-use';
import { privacyPolicy } from '../content/privacy-policy';
import './LegalModal.css';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'terms' | 'privacy';
}

export const LegalModal: React.FC<LegalModalProps> = ({ isOpen, onClose, type }) => {
  if (!isOpen) return null;

  const content = type === 'terms' ? termsOfUse : privacyPolicy;
  const title = type === 'terms' ? 'Terms of Use' : 'Privacy Policy';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-60" onClick={onClose} aria-hidden="true" data-testid="legal-modal-backdrop" />
        
        <div className="relative bg-widget-bg rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col border border-secondary">
          <div className="flex items-center justify-between p-6 border-b border-secondary">
            <h2 className="text-2xl font-semibold text-heading">{title}</h2>
            <button
              onClick={onClose}
              className="text-body-color hover:text-heading transition-colors p-1 hover:bg-body-bg/50 rounded"
              aria-label="Close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6">
            <div className="legal-content text-body-color">
              <div dangerouslySetInnerHTML={{ __html: formatMarkdown(content) }} />
            </div>
          </div>
          
          <div className="flex justify-end p-6 border-t border-secondary">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-primary hover:bg-primary-hover text-white rounded-md transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export function formatMarkdown(markdown: string): string {
  const blocks: string[] = [];
  let paragraph: string[] = [];
  let items: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push(`<p>${paragraph.join('\n')}</p>`);
      paragraph = [];
    }
  };
  const flushList = () => {
    if (items.length) {
      blocks.push(`<ul>${items.join('')}</ul>`);
      items = [];
    }
  };

  for (const raw of markdown.split('\n')) {
    const line = raw.trim();
    const heading = /^(#{1,3}) (.+)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      const level = heading[1].length;
      blocks.push(`<h${level}>${heading[2]}</h${level}>`);
      continue;
    }
    if (line.startsWith('- ')) {
      flushParagraph();
      items.push(`<li>${line.slice(2)}</li>`);
      continue;
    }
    flushList();
    if (line === '') {
      flushParagraph();
      continue;
    }
    paragraph.push(line);
  }
  flushParagraph();
  flushList();

  return blocks.join('').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}
